import React, { useState, useEffect } from 'react';
import {
  invoiceProcessingService,
  ProcessedInvoiceItem,
  ProcessingStats,
  InvoiceItemFilters,
} from '../services/invoiceProcessingService';
import { formatDate } from '../utils/date';

const InvoiceProcessingTab: React.FC = () => {
  const [stats, setStats] = useState<ProcessingStats | null>(null);
  const [processedItems, setProcessedItems] = useState<ProcessedInvoiceItem[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingItemId, setProcessingItemId] = useState<number | null>(null);
  const [previewItemId, setPreviewItemId] = useState<number | null>(null);
  const [previewResult, setPreviewResult] = useState<any | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Processing controls
  const [processingForm, setProcessingForm] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0], // today
    dryRun: false,
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Filters
  const [filters, setFilters] = useState<InvoiceItemFilters>({
    page: 1,
    pageSize: 20,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 20,
  });

  // Load initial data
  useEffect(() => {
    loadStats();
    loadProcessedItems();
    loadPendingCount();
  }, []);

  const loadStats = async () => {
    try {
      const response = await invoiceProcessingService.getStatusSummary(
        processingForm.startDate,
        processingForm.endDate
      );
      setStats(response);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadPendingCount = async () => {
    try {
      const response = await invoiceProcessingService.getPendingCount();
      setPendingCount(response.pendingCount);
    } catch (err) {
      console.error('Error loading pending count:', err);
    }
  };

  const loadProcessedItems = async () => {
    try {
      setLoading(true);
      const response = await invoiceProcessingService.getProcessedInvoiceItems(filters);
      setProcessedItems(response.items || []);
      setPagination({
        total: response.total || 0,
        totalPages: response.totalPages || 0,
        currentPage: response.currentPage || 1,
        pageSize: response.pageSize || 20,
      });
    } catch (err) {
      setError('Failed to load processed items');
      console.error('Error loading processed items:', err);
      setProcessedItems([]); // Ensure we always have an array
    } finally {
      setLoading(false);
    }
  };

  const handleFindUnprocessed = async () => {
    try {
      setIsProcessing(true);
      setError('');
      setSuccess('');

      const result = await invoiceProcessingService.findUnprocessedInvoices(processingForm);

      if (result.success) {
        setSuccess(`Found and imported ${result.imported} unprocessed invoices`);
        loadStats();
        loadProcessedItems();
        loadPendingCount();
      } else {
        setError('Failed to find unprocessed invoices');
      }
    } catch (err) {
      setError('Failed to find unprocessed invoices');
      console.error('Error finding unprocessed invoices:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProcessPending = async () => {
    try {
      setIsProcessing(true);
      setError('');
      setSuccess('');

      const result = await invoiceProcessingService.processPendingInvoices(processingForm.dryRun);

      if (result.success) {
        setSuccess(`Processed ${result.processed} pending invoices (${result.errors} errors)`);
        loadStats();
        loadProcessedItems();
        loadPendingCount();
      } else {
        setError('Failed to process pending invoices');
      }
    } catch (err) {
      setError('Failed to process pending invoices');
      console.error('Error processing pending invoices:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFilterChange = (key: keyof InvoiceItemFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filtering
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const canPreviewOrProcessItem = (status: string) =>
    status === 'pending' || status === 'failed' || status === 'processed_with_errors';

  const handleProcessItem = async (item: ProcessedInvoiceItem) => {
    if (!canPreviewOrProcessItem(item.processing_status)) {
      setError('Only pending, failed, or processed-with-errors items can be processed');
      return;
    }

    try {
      setProcessingItemId(item.id);
      setError('');
      setSuccess('');

      const result = await invoiceProcessingService.processItem(
        item.do_item_number.trim(),
        item.do_sop_number.trim(),
        false
      );

      if (result.success) {
        setSuccess(
          `Successfully processed item ${item.do_item_number} (SOP: ${item.do_sop_number})`
        );
        // Reload data
        loadStats();
        loadProcessedItems();
        loadPendingCount();
      } else {
        setError(`Failed to process item ${item.do_item_number}: ${result.data.errors.join(', ')}`);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message || err.message || 'Failed to process item';
      setError(`Failed to process item ${item.do_item_number}: ${errorMessage}`);
      console.error('Error processing item:', err);
    } finally {
      setProcessingItemId(null);
    }
  };

  const handlePreviewItem = async (item: ProcessedInvoiceItem) => {
    if (!canPreviewOrProcessItem(item.processing_status)) {
      setError('Only pending, failed, or processed-with-errors items can be previewed');
      return;
    }

    try {
      setPreviewItemId(item.id);
      setError('');

      const result = await invoiceProcessingService.processItem(
        item.do_item_number.trim(),
        item.do_sop_number.trim(),
        true // dryRun = true
      );

      setPreviewResult({
        item,
        result,
      });
      setShowPreview(true);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error?.message || err.message || 'Failed to preview item';
      setError(`Failed to preview item ${item.do_item_number}: ${errorMessage}`);
      console.error('Error previewing item:', err);
    } finally {
      setPreviewItemId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'failed':
        return '#dc3545';
      case 'unable_to_process':
        return '#ffc107';
      case 'processed_with_errors':
        return '#fd7e14';
      case 'processing':
        return '#007bff';
      case 'pending':
        return '#6c757d';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'unable_to_process':
        return 'Unable to Process';
      case 'processed_with_errors':
        return 'Processed with Errors';
      case 'processing':
        return 'Processing';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  // Apply filters when they change
  useEffect(() => {
    loadProcessedItems();
  }, [filters]);

  return (
    <div>
      {/* Error/Success Messages */}
      {error && (
        <div
          style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          {success}
        </div>
      )}

      {/* Processing Controls */}
      <div
        style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          border: '1px solid #dee2e6',
        }}
      >
        <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Invoice Processing</h3>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'end', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Start Date
            </label>
            <input
              type="date"
              value={processingForm.startDate}
              onChange={(e) =>
                setProcessingForm((prev) => ({ ...prev, startDate: e.target.value }))
              }
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              End Date
            </label>
            <input
              type="date"
              value={processingForm.endDate}
              onChange={(e) => setProcessingForm((prev) => ({ ...prev, endDate: e.target.value }))}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="dryRun"
              checked={processingForm.dryRun}
              onChange={(e) => setProcessingForm((prev) => ({ ...prev, dryRun: e.target.checked }))}
            />
            <label htmlFor="dryRun" style={{ margin: 0, fontWeight: '500' }}>
              Dry Run
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <button
            onClick={handleFindUnprocessed}
            disabled={isProcessing}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: isProcessing ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
            }}
          >
            {isProcessing ? 'Finding...' : 'Step 1: Find Unprocessed Invoices'}
          </button>

          <button
            onClick={handleProcessPending}
            disabled={isProcessing}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: isProcessing ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
            }}
          >
            {isProcessing ? 'Processing...' : 'Step 2: Process Pending Invoices'}
          </button>
        </div>

        {/* Pending Count Display */}
        <div
          style={{
            backgroundColor: pendingCount > 0 ? '#fff3cd' : '#d1ecf1',
            padding: '0.75rem',
            borderRadius: '6px',
            border: `1px solid ${pendingCount > 0 ? '#ffeaa7' : '#bee5eb'}`,
            marginTop: '1rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: pendingCount > 0 ? '#856404' : '#0c5460',
              }}
            >
              {pendingCount}
            </span>
            <span style={{ color: pendingCount > 0 ? '#856404' : '#0c5460' }}>
              {pendingCount === 1 ? 'invoice item' : 'invoice items'} pending processing
            </span>
            {pendingCount > 0 && (
              <span
                style={{
                  fontSize: '0.875rem',
                  color: '#856404',
                  fontStyle: 'italic',
                }}
              >
                (Click "Step 2" to process them)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem',
          }}
        >
          <div
            style={{
              backgroundColor: '#e3f2fd',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #bbdefb',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2' }}>Total Items</h4>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2' }}>
              {stats.total_invoice_items}
            </p>
          </div>

          <div
            style={{
              backgroundColor: '#e8f5e8',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #c8e6c9',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#388e3c' }}>Completed</h4>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#388e3c' }}>
              {stats.processed_invoice_items}
            </p>
          </div>

          <div
            style={{
              backgroundColor: '#fff3e0',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #ffcc02',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#f57c00' }}>With Errors</h4>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#f57c00' }}>
              {stats.processed_with_errors}
            </p>
          </div>

          <div
            style={{
              backgroundColor: '#fce4ec',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid #f8bbd9',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#c2185b' }}>Success Rate</h4>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#c2185b' }}>
              {stats.successRate}%
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #dee2e6',
        }}
      >
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              SOP Number
            </label>
            <input
              type="text"
              placeholder="Filter by SOP number"
              value={filters.do_sop_number || ''}
              onChange={(e) => handleFilterChange('do_sop_number', e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
                width: '150px',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Product Code
            </label>
            <input
              type="text"
              placeholder="Filter by product code"
              value={filters.do_item_number || ''}
              onChange={(e) => handleFilterChange('do_item_number', e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
                width: '150px',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Status
            </label>
            <select
              value={filters.processing_status || ''}
              onChange={(e) => handleFilterChange('processing_status', e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '1rem',
                width: '150px',
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="unable_to_process">Unable to Process</option>
              <option value="processed_with_errors">Processed with Errors</option>
            </select>
          </div>

          <button
            onClick={() => setFilters({ page: 1, pageSize: 20 })}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Processed Items Table */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '1rem',
            borderBottom: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
          }}
        >
          <h3 style={{ margin: 0, color: '#495057' }}>Invoice Items</h3>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem',
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      SOP Number
                    </th>
                    <th
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      Product Code
                    </th>
                    <th
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      Quantity
                    </th>
                    <th
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      Processed At
                    </th>
                    <th
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      Error Message
                    </th>
                    <th
                      style={{
                        padding: '0.75rem',
                        textAlign: 'left',
                        borderBottom: '1px solid #dee2e6',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(processedItems || []).map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                      <td style={{ padding: '0.75rem' }}>{item.do_sop_number}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <span
                          title={item.product_name || 'Product name not available'}
                          style={{
                            cursor: item.product_name ? 'help' : 'default',
                            textDecoration: item.product_name ? 'underline' : 'none',
                            textDecorationStyle: 'dotted',
                          }}
                        >
                          {item.do_item_number}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem' }}>{item.do_quantity_sold}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <span
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: getStatusColor(item.processing_status),
                            color: 'white',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                          }}
                        >
                          {getStatusText(item.processing_status)}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem' }}>{formatDate(item.processed_at)}</td>
                      <td style={{ padding: '0.75rem', maxWidth: '200px', wordWrap: 'break-word' }}>
                        {item.error_message || '-'}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {canPreviewOrProcessItem(item.processing_status) && (
                            <>
                              <button
                                onClick={() => handlePreviewItem(item)}
                                disabled={previewItemId === item.id || processingItemId === item.id}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  backgroundColor:
                                    previewItemId === item.id
                                      ? '#6c757d'
                                      : processingItemId === item.id
                                        ? '#6c757d'
                                        : '#17a2b8',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor:
                                    previewItemId === item.id || processingItemId === item.id
                                      ? 'not-allowed'
                                      : 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                }}
                              >
                                {previewItemId === item.id ? 'Previewing...' : 'Preview'}
                              </button>
                              <button
                                onClick={() => handleProcessItem(item)}
                                disabled={processingItemId === item.id || previewItemId === item.id}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  backgroundColor:
                                    processingItemId === item.id
                                      ? '#6c757d'
                                      : previewItemId === item.id
                                        ? '#6c757d'
                                        : item.processing_status === 'pending'
                                          ? '#007bff'
                                          : '#ffc107',
                                  color:
                                    item.processing_status === 'failed' ||
                                    item.processing_status === 'processed_with_errors'
                                      ? '#212529'
                                      : 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor:
                                    processingItemId === item.id || previewItemId === item.id
                                      ? 'not-allowed'
                                      : 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                }}
                              >
                                {processingItemId === item.id
                                  ? 'Processing...'
                                  : item.processing_status === 'pending'
                                    ? 'Process'
                                    : 'Retry'}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(processedItems || []).length === 0 && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                No Invoice Items found
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div
            style={{
              padding: '1rem',
              borderTop: '1px solid #dee2e6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.pageSize, pagination.total)} of{' '}
              {pagination.total} entries
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                style={{
                  padding: '0.5rem',
                  backgroundColor: pagination.currentPage === 1 ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                Previous
              </button>

              <span
                style={{
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                style={{
                  padding: '0.5rem',
                  backgroundColor:
                    pagination.currentPage === pagination.totalPages ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && previewResult && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowPreview(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h2 style={{ margin: 0, color: '#495057' }}>Processing Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6c757d',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>Invoice Item</h3>
              <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>SOP Number:</strong> {previewResult.item.do_sop_number}
                </p>
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Product Code:</strong> {previewResult.item.do_item_number}
                </p>
                {previewResult.item.product_name && (
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Product Name:</strong> {previewResult.item.product_name}
                  </p>
                )}
                <p style={{ margin: '0.25rem 0' }}>
                  <strong>Quantity (Cases):</strong> {previewResult.item.do_quantity_sold}
                </p>
              </div>
            </div>

            {/* Stock Information */}
            {previewResult.result.data?.preview && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>Stock Information</h3>
                <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                    <div>
                      <p style={{ margin: '0.25rem 0', fontWeight: 'bold', color: '#495057' }}>
                        Current Stock
                      </p>
                      <p style={{ margin: '0.25rem 0' }}>
                        {previewResult.result.data.preview.currentStock.cases} cases,{' '}
                        {previewResult.result.data.preview.currentStock.units} units
                      </p>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#6c757d' }}>
                        ({previewResult.result.data.preview.currentStock.totalUnits} total units)
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: '0.25rem 0', fontWeight: 'bold', color: '#dc3545' }}>
                        To Deduct
                      </p>
                      <p style={{ margin: '0.25rem 0' }}>
                        {previewResult.result.data.preview.toDeduct.cases} cases,{' '}
                        {previewResult.result.data.preview.toDeduct.units} units
                      </p>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#6c757d' }}>
                        ({previewResult.result.data.preview.toDeduct.totalUnits} total units)
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: '0.25rem 0', fontWeight: 'bold', color: '#28a745' }}>
                        Would Result In
                      </p>
                      <p style={{ margin: '0.25rem 0' }}>
                        {previewResult.result.data.preview.wouldResultIn.cases} cases,{' '}
                        {previewResult.result.data.preview.wouldResultIn.units} units
                      </p>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#6c757d' }}>
                        ({previewResult.result.data.preview.wouldResultIn.totalUnits} total units)
                      </p>
                    </div>
                  </div>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6c757d' }}>
                    Units per case: {previewResult.result.data.preview.unitsPerCase}
                  </p>
                  {previewResult.result.data.preview.inventoryBatches &&
                    previewResult.result.data.preview.inventoryBatches.length > 0 && (
                      <div style={{ marginTop: '1rem' }}>
                        <p style={{ margin: '0.5rem 0', fontWeight: 'bold', fontSize: '0.875rem' }}>
                          Inventory Batches (FIFO order):
                        </p>
                        <ul
                          style={{
                            margin: '0.25rem 0',
                            paddingLeft: '1.5rem',
                            fontSize: '0.875rem',
                          }}
                        >
                          {previewResult.result.data.preview.inventoryBatches.map(
                            (batch: any, idx: number) => (
                              <li key={batch.id}>
                                Batch {idx + 1}: {batch.casesAvailable} cases,{' '}
                                {batch.unitsAvailable} units (Expires:{' '}
                                {new Date(batch.expiryDate).toLocaleDateString()})
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              </div>
            )}

            {previewResult.result.success ? (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: '#28a745' }}>✓ Preview Successful</h3>
                <div style={{ backgroundColor: '#d4edda', padding: '1rem', borderRadius: '4px' }}>
                  <p style={{ margin: '0.5rem 0', color: '#155724' }}>
                    This item can be processed successfully. The following actions would occur:
                  </p>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', color: '#155724' }}>
                    {previewResult.result.data?.inventory_adjustment && (
                      <li>Inventory would be deducted using FIFO logic</li>
                    )}
                    {previewResult.result.data?.preview && (
                      <li>
                        Inventory would be deducted:{' '}
                        {previewResult.result.data.preview.toDeduct.cases} cases (
                        {previewResult.result.data.preview.toDeduct.totalUnits} units)
                      </li>
                    )}
                    {previewResult.result.data?.production_order && (
                      <li>A regular production order would be created</li>
                    )}
                    {previewResult.result.data?.emergency_production_order && (
                      <li>
                        <strong>Emergency production order would be created</strong> (insufficient
                        inventory)
                      </li>
                    )}
                    {previewResult.result.data?.regular_production_order && (
                      <li>A restocking production order would be created</li>
                    )}
                    {!previewResult.result.data?.inventory_adjustment &&
                      !previewResult.result.data?.production_order &&
                      !previewResult.result.data?.emergency_production_order &&
                      !previewResult.result.data?.preview && (
                        <li>
                          Item would be processed (no inventory adjustment or production order
                          needed)
                        </li>
                      )}
                  </ul>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: '#dc3545' }}>
                  {previewResult.result.data?.preview?.insufficientInventory
                    ? '⚠️ Insufficient Inventory'
                    : '✗ Preview Failed'}
                </h3>
                <div style={{ backgroundColor: '#f8d7da', padding: '1rem', borderRadius: '4px' }}>
                  {previewResult.result.data?.preview?.insufficientInventory ? (
                    <div>
                      <p style={{ margin: '0.5rem 0', color: '#721c24', fontWeight: 'bold' }}>
                        This item cannot be processed due to insufficient inventory.
                      </p>
                      {previewResult.result.data.preview.shortage && (
                        <div
                          style={{
                            margin: '0.5rem 0',
                            padding: '0.75rem',
                            backgroundColor: '#fff3cd',
                            borderRadius: '4px',
                            border: '1px solid #ffc107',
                          }}
                        >
                          <p style={{ margin: '0.25rem 0', color: '#856404', fontWeight: 'bold' }}>
                            Shortage: {previewResult.result.data.preview.shortage.cases} cases (
                            {previewResult.result.data.preview.shortage.totalUnits} units)
                          </p>
                          <p style={{ margin: '0.5rem 0 0 0', color: '#856404' }}>
                            If processed, a production order would be created to fulfill this
                            invoice. The invoice would be marked as "waiting for inventory" until
                            the production order is completed and inventory is available.
                          </p>
                        </div>
                      )}
                      <p style={{ margin: '0.5rem 0 0 0', color: '#721c24', fontSize: '0.875rem' }}>
                        Errors:
                      </p>
                      <ul style={{ margin: '0.25rem 0', paddingLeft: '1.5rem', color: '#721c24' }}>
                        {previewResult.result.data.errors?.map((err: string, idx: number) => (
                          <li key={idx} style={{ fontSize: '0.875rem' }}>
                            {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <>
                      <p style={{ margin: '0.5rem 0', color: '#721c24' }}>
                        This item cannot be processed. Errors:
                      </p>
                      <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', color: '#721c24' }}>
                        {previewResult.result.data.errors?.map((err: string, idx: number) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceProcessingTab;
